import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton
} from "@humanex/ui/components";
import { cn, useI18n } from "@humanex/ui/libs";
import { useRouter } from "@tanstack/react-router";
import { ChevronDown, CircleCheckBig, LogOut, User } from "lucide-react";

import { trackEngagement } from "@/libs/analytics";
import { firebaseService } from "@/libs/firebase";
import { useAuthStore } from "@/stores/auth.store";

export function AuthButton() {
  const { loggedIn, curUser, loading } = useAuthStore();
  const router = useRouter();
  const { t } = useI18n();

  const handleSignOut = async () => {
    trackEngagement("sign_out", "authentication");
    await firebaseService.signOut();
    router.navigate({ to: "/" });
  };

  if (loading) {
    return <Skeleton className="w-9 h-9" />;
  }

  if (loggedIn) {
    return (
      <DropdownMenu closeAfterClick>
        <DropdownMenuTrigger asChild>
          {(isOpen) => (
            <button className="pr-1 w-10 h-9 relative">
              <Avatar
                name={curUser?.fbUser?.email || undefined}
                src={curUser?.profile?.avatar || undefined}
                size={9}
              />
              <div className="absolute right-0 bottom-0 bg-primary-100 rounded-full border border-white/10">
                <ChevronDown
                  strokeWidth="1.5"
                  className={cn(
                    "transition-transform duration-200 ease-in-out w-3 h-3",
                    isOpen && "rotate-180"
                  )}
                />
              </div>
            </button>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent align="right">
          <DropdownMenuItem
            icon={<User size={18} strokeWidth={2} />}
            onClick={() => router.navigate({ to: "/profile" })}
          >
            {t("navigation.viewProfile")}
          </DropdownMenuItem>

          <DropdownMenuDivider />

          <DropdownMenuItem
            icon={<LogOut size={18} strokeWidth={2} />}
            onClick={handleSignOut}
            variant="danger"
          >
            {t("navigation.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    /* Sign In Button */
    <Button
      onClick={() => {
        trackEngagement("sign_in_click", "authentication");
        firebaseService.signIn();
      }}
      className="flex items-center space-x-1 md:space-x-2 text-responsive-base px-2 py-1.5 md:px-3 md:py-2"
      variant="outline"
    >
      <CircleCheckBig size={16} />
      <span>{t("navigation.login")}</span>
    </Button>
  );
}
